import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Paperclip, Trash2, Loader2, Mail, Building2, Clock, Bold, Italic, List as ListIcon, Type } from "lucide-react";
import { API_BASE, formatApiError } from "./AuthContext";

const INPUT_CLS =
  "w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[14px] focus:outline-none focus:border-[var(--ink)]";

// Tiny markdown → HTML converter. Handles **bold**, *italic*, `- bullet` lines,
// and `[size:large|small]text[/size]` for font-sizing. Multi-line paragraphs
// become <p>; single-line breaks become <br>.
function mdToHtml(md) {
  if (!md) return "";
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Split into blocks separated by blank lines
  const blocks = md.split(/\n{2,}/);
  return blocks.map((block) => {
    const lines = block.split("\n");
    // Bullet list block: every line starts with "- "
    if (lines.every((ln) => /^\s*-\s+/.test(ln))) {
      const items = lines.map((ln) => `<li>${formatInline(esc(ln.replace(/^\s*-\s+/, "")))}</li>`).join("");
      return `<ul style="margin:0 0 12px 20px;padding:0;">${items}</ul>`;
    }
    return `<p style="margin:0 0 12px 0;">${lines.map((ln) => formatInline(esc(ln))).join("<br/>")}</p>`;
  }).join("");
}
function formatInline(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/\[size:large\]([\s\S]+?)\[\/size\]/g, '<span style="font-size:18px;">$1</span>')
    .replace(/\[size:small\]([\s\S]+?)\[\/size\]/g, '<span style="font-size:11px;">$1</span>');
}

/**
 * Unified email compose dialog. Used on Lead / Client / Job detail pages.
 *
 * Props:
 *   open: boolean
 *   onClose: fn
 *   onSent: fn(result)
 *   context: { lead_id?, client_id?, job_id? }
 *   defaultTo: string (recipient email, prefilled)
 *   defaultToName: string (display name e.g. "Acme Pty Ltd")
 */
export default function ComposeEmailDialog({
  open,
  onClose,
  onSent,
  context = {},
  defaultTo = "",
  defaultToName = "",
  defaultCc = "",
  defaultSubject = "",
  defaultBody = "",
}) {
  const [fromOptions, setFromOptions] = useState([]);
  const [defaultConnectionId, setDefaultConnectionId] = useState(null);
  const [resendAvailable, setResendAvailable] = useState(false);
  const [firmFromAddr, setFirmFromAddr] = useState("");
  const [channel, setChannel] = useState("gmail"); // gmail | resend
  const [connectionId, setConnectionId] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");

  const [to, setTo] = useState(defaultTo || "");
  const [cc, setCc] = useState(defaultCc || "");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState(defaultSubject || "");
  const [body, setBody] = useState(defaultBody || "");
  const [attachments, setAttachments] = useState([]); // [{key, filename, size, content_type, storage_kind}]

  const [showCcBcc, setShowCcBcc] = useState(!!defaultCc);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(""); // "YYYY-MM-DDTHH:mm" local
  const bodyRef = useRef(null);

  // Rich-text toolbar: wraps the textarea selection with markdown chars.
  // The backend converts markdown to HTML when `body_html` is set on send.
  const applyMarkdown = (kind) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const before = body.slice(0, start);
    const sel = body.slice(start, end);
    const after = body.slice(end);
    let wrapped = sel; let caret = end;
    if (kind === "bold") { wrapped = `**${sel || "bold text"}**`; caret = start + wrapped.length; }
    else if (kind === "italic") { wrapped = `*${sel || "italic text"}*`; caret = start + wrapped.length; }
    else if (kind === "bullet") {
      // Prefix each selected line with "- "; if no selection, insert one bullet line
      if (sel) wrapped = sel.split("\n").map((ln) => ln.startsWith("- ") ? ln : `- ${ln}`).join("\n");
      else wrapped = `- list item`;
      caret = start + wrapped.length;
    }
    else if (kind === "size-large") { wrapped = `[size:large]${sel || "larger text"}[/size]`; caret = start + wrapped.length; }
    else if (kind === "size-small") { wrapped = `[size:small]${sel || "smaller text"}[/size]`; caret = start + wrapped.length; }
    const next = before + wrapped + after;
    setBody(next);
    setTimeout(() => { el.focus(); el.setSelectionRange(caret, caret); }, 0);
  };

  useEffect(() => {
    if (!open) return;
    setTo(defaultTo || "");
    setCc("");
    setBcc("");
    setSubject("");
    setBody("");
    setTemplateId("");
    setAttachments([]);
    setShowCcBcc(false);
    setScheduleEnabled(false);
    setScheduleAt("");
  }, [open, defaultTo]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [optRes, tplRes] = await Promise.all([
          axios.get(`${API_BASE}/outbound-email/from-options`),
          axios.get(`${API_BASE}/email-templates?enabled=true`),
        ]);
        setFromOptions(optRes.data.options || []);
        setDefaultConnectionId(optRes.data.default_connection_id || null);
        setResendAvailable(!!optRes.data.resend_available);
        setFirmFromAddr(optRes.data.firm_from_address || "");
        if (optRes.data.default_connection_id) {
          setConnectionId(optRes.data.default_connection_id);
          setChannel("gmail");
        } else if (optRes.data.resend_available) {
          setChannel("resend");
          setConnectionId(null);
        }
        setTemplates(tplRes.data || []);
      } catch (err) {
        toast.error(formatApiError(err) || "Failed to load compose options");
      }
    })();
  }, [open]);

  const applyTemplate = async (tplId) => {
    setTemplateId(tplId);
    if (!tplId) {
      setSubject("");
      setBody("");
      return;
    }
    try {
      const r = await axios.post(`${API_BASE}/outbound-email/render-template`, {
        template_id: tplId,
        ...context,
      });
      setSubject(r.data.subject || "");
      setBody(r.data.body || "");
    } catch (err) {
      toast.error(formatApiError(err) || "Could not render template");
    }
  };

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    if (attachments.length + fileList.length > 5) {
      toast.error("Max 5 attachments per email");
      return;
    }
    setUploading(true);
    try {
      for (const f of Array.from(fileList)) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`"${f.name}" exceeds 10 MB`);
          continue;
        }
        const form = new FormData();
        form.append("file", f);
        const r = await axios.post(`${API_BASE}/outbound-email/attachments`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachments((prev) => [...prev, r.data]);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Attachment upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const splitEmails = (raw) =>
    raw
      .split(/[,;\n]/)
      .map((x) => x.trim())
      .filter(Boolean);

  const selectedConn = useMemo(
    () => fromOptions.find((o) => o.connection_id === connectionId),
    [fromOptions, connectionId],
  );

  const fromLabel = () => {
    if (channel === "resend") {
      return `${firmFromAddr || "Firm domain"} (Resend)`;
    }
    if (selectedConn) {
      const kindBadge = selectedConn.kind === "shared" ? " · Shared" : " · Personal";
      return `${selectedConn.gmail_address}${kindBadge}`;
    }
    return "Pick a connection";
  };

  const canSend = useMemo(() => {
    if (busy || uploading) return false;
    if (channel === "gmail") return !!connectionId;
    if (channel === "resend") return resendAvailable;
    return false;
  }, [busy, uploading, channel, connectionId, resendAvailable]);

  const send = async () => {
    const toList = splitEmails(to);
    if (toList.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    if (channel === "gmail" && !connectionId) {
      toast.error("Pick a Gmail connection to send from");
      return;
    }
    let sendAtIso = null;
    if (scheduleEnabled) {
      if (!scheduleAt) {
        toast.error("Pick a date/time to schedule");
        return;
      }
      const when = new Date(scheduleAt);
      if (Number.isNaN(when.getTime())) {
        toast.error("Invalid schedule date/time");
        return;
      }
      if (when.getTime() <= Date.now() + 30 * 1000) {
        toast.error("Schedule must be at least 30s in the future");
        return;
      }
      sendAtIso = when.toISOString();
    }
    setBusy(true);
    try {
      const payload = {
        to: toList,
        cc: splitEmails(cc),
        bcc: splitEmails(bcc),
        subject,
        body,
        body_html: mdToHtml(body),
        channel,
        connection_id: channel === "gmail" ? connectionId : null,
        template_id: templateId || null,
        attachments,
        context,
        send_at: sendAtIso,
      };
      const r = await axios.post(`${API_BASE}/outbound-email/send`, payload);
      if (r.data?.scheduled) {
        toast.success(
          `Scheduled for ${new Date(r.data.scheduled_for).toLocaleString()}`,
        );
      } else {
        toast.success(
          `Email sent via ${channel}${r.data?.result?.via_connection ? ` (${r.data.result.via_connection})` : ""}`,
        );
      }
      onSent?.(r.data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  // YYYY-MM-DDTHH:mm — used by datetime-local input
  const minScheduleAt = useMemo(() => {
    const d = new Date(Date.now() + 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  if (!open) return null;

  const headerSubtitle = defaultToName
    ? `Compose · to ${defaultToName}`
    : "Compose email";

  return (
    <div
      className="fixed inset-0 z-[70] bg-[var(--ink)]/40 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="compose-email-dialog"
    >
      <div
        className="bg-[var(--paper)] rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto border border-[var(--border)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between sticky top-0 bg-[var(--paper)] z-10">
          <div className="flex items-center gap-3">
            <Mail size={16} className="text-[var(--ink-3)]" />
            <div>
              <div className="overline text-[10px]">{headerSubtitle}</div>
              <div className="text-[12px] text-[var(--ink-3)] font-mono">
                From: {fromLabel()}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--ink-3)] hover:text-[var(--ink)]"
            data-testid="compose-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-3">
          {/* From / Channel picker */}
          <div className="bg-[var(--subtle)] border border-[var(--border)] rounded-md p-3 space-y-2">
            <div className="overline mb-1 text-[10px] flex items-center gap-1">
              <Building2 size={11} /> Send from
            </div>
            <div className="flex flex-wrap gap-2">
              {fromOptions.map((opt) => {
                const active = channel === "gmail" && connectionId === opt.connection_id;
                return (
                  <button
                    key={opt.connection_id}
                    onClick={() => {
                      setChannel("gmail");
                      setConnectionId(opt.connection_id);
                    }}
                    className={`text-[11px] px-3 py-1.5 rounded-full border transition ${
                      active
                        ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                        : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
                    }`}
                    data-testid={`compose-from-${opt.connection_id}`}
                    title={opt.label || opt.gmail_address}
                  >
                    <span className="font-mono">{opt.gmail_address}</span>
                    <span className="ml-1 opacity-75">
                      {opt.kind === "shared" ? "· Shared" : "· Personal"}
                      {opt.connection_id === defaultConnectionId ? " · default" : ""}
                    </span>
                  </button>
                );
              })}
              {resendAvailable && (
                <button
                  onClick={() => {
                    setChannel("resend");
                    setConnectionId(null);
                  }}
                  className={`text-[11px] px-3 py-1.5 rounded-full border transition ${
                    channel === "resend"
                      ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                      : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
                  }`}
                  data-testid="compose-from-resend"
                >
                  {firmFromAddr || "Firm domain"} <span className="opacity-75">· Resend</span>
                </button>
              )}
              {fromOptions.length === 0 && !resendAvailable && (
                <div className="text-[11px] text-[var(--ink-3)]">
                  No outbound channels configured. Connect a Gmail or set RESEND_API_KEY.
                </div>
              )}
            </div>
          </div>

          {/* Template picker */}
          <label className="block">
            <div className="overline mb-1 text-[10px]">Use template (optional)</div>
            <select
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className={INPUT_CLS}
              data-testid="compose-template-select"
            >
              <option value="">— Write from scratch —</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>

          {/* To */}
          <label className="block">
            <div className="overline mb-1 text-[10px] flex items-center justify-between">
              <span>To</span>
              <button
                onClick={() => setShowCcBcc((s) => !s)}
                className="normal-case tracking-normal text-[10px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                data-testid="compose-toggle-ccbcc"
                type="button"
              >
                {showCcBcc ? "Hide" : "Add"} Cc / Bcc
              </button>
            </div>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="someone@example.com"
              className={INPUT_CLS}
              data-testid="compose-to"
            />
          </label>

          {showCcBcc && (
            <>
              <label className="block">
                <div className="overline mb-1 text-[10px]">Cc</div>
                <input
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="comma-separated"
                  className={INPUT_CLS}
                  data-testid="compose-cc"
                />
              </label>
              <label className="block">
                <div className="overline mb-1 text-[10px]">Bcc</div>
                <input
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  placeholder="comma-separated"
                  className={INPUT_CLS}
                  data-testid="compose-bcc"
                />
              </label>
            </>
          )}

          {/* Subject */}
          <label className="block">
            <div className="overline mb-1 text-[10px]">Subject</div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={INPUT_CLS}
              data-testid="compose-subject"
            />
          </label>

          {/* Body */}
          <label className="block">
            <div className="overline mb-1 text-[10px]">Body</div>
            <div className="flex items-center gap-1 mb-1 flex-wrap" data-testid="compose-toolbar">
              <button type="button" onClick={() => applyMarkdown("bold")}
                      title="Bold (**text**)" data-testid="compose-toolbar-bold"
                      className="text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)] p-1.5 rounded inline-flex items-center">
                <Bold size={13}/>
              </button>
              <button type="button" onClick={() => applyMarkdown("italic")}
                      title="Italic (*text*)" data-testid="compose-toolbar-italic"
                      className="text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)] p-1.5 rounded inline-flex items-center">
                <Italic size={13}/>
              </button>
              <button type="button" onClick={() => applyMarkdown("bullet")}
                      title="Bullet list (- item)" data-testid="compose-toolbar-bullet"
                      className="text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)] p-1.5 rounded inline-flex items-center">
                <ListIcon size={13}/>
              </button>
              <div className="border-l border-[var(--border)] h-4 mx-1" aria-hidden/>
              <button type="button" onClick={() => applyMarkdown("size-small")}
                      title="Small text" data-testid="compose-toolbar-small"
                      className="text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)] px-1.5 py-1 rounded text-[10px] inline-flex items-center gap-0.5">
                <Type size={11}/> small
              </button>
              <button type="button" onClick={() => applyMarkdown("size-large")}
                      title="Large text" data-testid="compose-toolbar-large"
                      className="text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--subtle)] px-1.5 py-1 rounded text-[13px] inline-flex items-center gap-0.5">
                <Type size={14}/> large
              </button>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              ref={bodyRef}
              rows={10}
              className={`${INPUT_CLS} text-[13px]`}
              placeholder="Write your message…  Use **bold**, *italic*, and - bullet"
              data-testid="compose-body"
            />
            <div className="text-[10px] text-[var(--ink-3)] mt-1">
              Formatting: <code>**bold**</code> · <code>*italic*</code> · lines starting with <code>- </code> become bullets · <code>[size:large]…[/size]</code>.
              Merge fields like <code>{"{{client_name}}"}</code> are filled when you pick a template above.
            </div>
          </label>

          {/* Attachments */}
          <div>
            <div className="overline mb-1 text-[10px] flex items-center justify-between">
              <span>Attachments</span>
              <label
                className="normal-case tracking-normal text-[10px] text-[var(--accent)] hover:underline cursor-pointer inline-flex items-center gap-1"
                data-testid="compose-attach-label"
              >
                <Paperclip size={11} />
                {uploading ? "Uploading…" : "Attach files"}
                <input
                  type="file"
                  className="hidden"
                  multiple
                  onChange={(e) => {
                    uploadFiles(e.target.files);
                    e.target.value = "";
                  }}
                  disabled={uploading}
                  data-testid="compose-attach-input"
                />
              </label>
            </div>
            {attachments.length > 0 ? (
              <ul className="space-y-1.5">
                {attachments.map((a, i) => (
                  <li
                    key={a.key}
                    className="flex items-center justify-between gap-2 text-[12px] bg-white border border-[var(--border)] rounded-md px-3 py-1.5"
                    data-testid={`compose-attachment-${i}`}
                  >
                    <span className="font-mono truncate text-[var(--ink-2)]">
                      {a.filename}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-mono text-[var(--ink-3)]">
                        {(a.size / 1024).toFixed(1)} KB
                      </span>
                      <button
                        onClick={() => removeAttachment(i)}
                        className="text-[var(--ink-3)] hover:text-[#B91C1C]"
                        type="button"
                        aria-label="Remove attachment"
                        data-testid={`compose-attachment-remove-${i}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-[11px] text-[var(--ink-3)]">
                No attachments. Max 5 files · 10 MB each.
              </div>
            )}
          </div>

          {/* Schedule send */}
          <div className="bg-[var(--subtle)] border border-[var(--border)] rounded-md p-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                className="accent-[var(--accent)]"
                data-testid="compose-schedule-toggle"
              />
              <Clock size={12} className="text-[var(--ink-3)]" />
              <span className="text-[12px] text-[var(--ink-2)]">
                Schedule send for later
              </span>
            </label>
            {scheduleEnabled && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  min={minScheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  className={`${INPUT_CLS} max-w-[260px]`}
                  data-testid="compose-schedule-at"
                />
                <span className="text-[10px] font-mono text-[var(--ink-3)]">
                  {scheduleAt
                    ? `→ ${new Date(scheduleAt).toLocaleString()}`
                    : "Pick a future date/time"}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[var(--border)] flex justify-between items-center sticky bottom-0 bg-[var(--paper)]">
          <div className="text-[11px] text-[var(--ink-3)] font-mono">
            {channel === "gmail" && selectedConn
              ? `Sending as ${selectedConn.gmail_address}`
              : channel === "resend"
              ? "Sending via firm domain"
              : ""}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="btn-secondary"
              data-testid="compose-cancel"
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={!canSend}
              className="btn-primary"
              data-testid="compose-send"
              type="button"
            >
              {busy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : scheduleEnabled ? (
                <Clock size={14} />
              ) : (
                <Send size={14} />
              )}
              {busy
                ? scheduleEnabled ? "Scheduling…" : "Sending…"
                : scheduleEnabled ? "Schedule send" : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
